/**
 * EntityPickerMenu
 *
 * A reusable search-and-select Menu component for adding entities to a scene or linking.
 * Renders a sticky search field + filtered list of available items, grouped by category.
 */
import { Search as SearchIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

interface PickableItem {
  id: string;
  name: string;
  category?: string;
}

interface EntityPickerMenuProps {
  anchorEl: HTMLElement | null;
  items: PickableItem[];
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onSelect: (id: string) => void;
  onClose: () => void;
}

export function EntityPickerMenu({
  anchorEl,
  items,
  searchQuery,
  onSearchChange,
  onSelect,
  onClose,
}: EntityPickerMenuProps) {
  const { t } = useTranslation();

  const groupedItems = useMemo(() => {
    const groups: Record<string, PickableItem[]> = {};
    const uncategorized: PickableItem[] = [];

    items.forEach(item => {
      if (item.category) {
        if (!groups[item.category]) groups[item.category] = [];
        groups[item.category].push(item);
      } else {
        uncategorized.push(item);
      }
    });

    return { groups, uncategorized };
  }, [items]);

  return (
    <Popover
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      PaperProps={{ sx: { width: 250, maxHeight: 400, display: 'flex', flexDirection: 'column' } }}
    >
      <Box sx={{ p: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={t('dashboard.combobox.search')}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()} // Prevent list navigation from stealing keys
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Divider />
      
      <List dense sx={{ flexGrow: 1, overflowY: 'auto', p: 0 }}>
        {items.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">{t('dashboard.combobox.no_options')}</Typography>
          </Box>
        ) : (
          <>
            {Object.entries(groupedItems.groups).map(([category, catItems]) => (
              <Box key={category}>
                <ListSubheader sx={{ lineHeight: '32px', bgcolor: 'background.default', fontWeight: 'bold' }}>
                  {category}
                </ListSubheader>
                {catItems.map((item) => (
                  <ListItemButton
                    key={item.id}
                    onClick={() => {
                      onSelect(item.id);
                      onClose();
                    }}
                  >
                    <ListItemText primary={item.name} primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItemButton>
                ))}
              </Box>
            ))}
            
            {groupedItems.uncategorized.length > 0 && (
              <Box>
                {Object.keys(groupedItems.groups).length > 0 && (
                  <ListSubheader sx={{ lineHeight: '32px', bgcolor: 'background.default', fontWeight: 'bold' }}>
                    {t('common.other')}
                  </ListSubheader>
                )}
                {groupedItems.uncategorized.map((item) => (
                  <ListItemButton
                    key={item.id}
                    onClick={() => {
                      onSelect(item.id);
                      onClose();
                    }}
                  >
                    <ListItemText primary={item.name} primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItemButton>
                ))}
              </Box>
            )}
          </>
        )}
      </List>
    </Popover>
  );
}
